import { queryKeys } from '@query/keys'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { getScrollToCardId } from '@utils/getItemId'
import { TransactionType } from '@utils/transaction.utils'

import { TransactionDto } from '@model/TransactionDto'

import { Loading } from '@shared/components/Loading/Loading'
import { TransactionHistoryDrawerModal } from '@shared/components/TransactionHistoryDrawer'
import {
  TransactionHistoryEmptyState,
  TransactionHistoryMobileRow,
  TransactionHistoryRowCells,
} from '@shared/components/TransactionHistoryTable'
import pageStyles from '@shared/components/TransactionHistoryTable/transactionHistoryTable.module.scss'
import { ActionModal } from '@shared/modals/Action.modal'

import type { IAppendableFetchQueryResult, SearchResponseProps } from '../IBReportResultsPage'
import { IBReportsTable, type IBReportsTableSortOption } from '../IBReportsTable'

export type IBTransactionHistorySortKeys =
  | 'TransactionType.name'
  | 'CreatedDate'
  | 'TransactionState.name'

interface Props {
  data: IAppendableFetchQueryResult<TransactionDto>
  forceRefresh: (skipLoadingFlag?: boolean) => void
  walletId?: string
  search: Omit<SearchResponseProps, 'sortOptions'> & {
    sortOptions: IBReportsTableSortOption<IBTransactionHistorySortKeys | null>[]
  }
}

export const IBTransactionHistoryTable: React.FC<Props> = (props) => {
  const { data: dealSummary, forceRefresh, search, walletId } = props
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const isMobile = useWindowResize()
  const locale = useSessionLanguage()
  const isLoading = dealSummary.query.isFetching
  const tableQuery = { ...dealSummary.query, isLoading }

  const confirmCancelTransactionModal = useModal(ActionModal, {
    title: t('Transactions.Cancel Transaction'),
    description: t('Transactions.Are you sure you want to cancel your transaction'),
  })

  const onCancelTransaction = async (tx: TransactionDto) => {
    if (tx.type.id === TransactionType.WithdrawToPaymentAgent) {
      await apiClient.cancelTransaction(tx.id)
    } else {
      await apiClient.cancelWithdrawal(tx.id)
    }
    queryClient.invalidateQueries({ queryKey: ['transactionHistory'] })
    queryClient.invalidateQueries({ queryKey: ['wallet'] })
    queryClient.invalidateQueries({
      queryKey: queryKeys.transaction.statusHistory(tx.id, locale),
    })
    confirmCancelTransactionModal.close()
    forceRefresh()
  }

  const drawerModal = useModal(TransactionHistoryDrawerModal, () => ({
    context: 'ib' as const,
    transactions: dealSummary.query.data,
    walletId,
    onCancelTransaction: (tx: TransactionDto) => {
      confirmCancelTransactionModal.open({
        onConfirm: () => onCancelTransaction(tx),
      })
    },
  }))

  const openTransactionDrawer = (tx: TransactionDto) =>
    drawerModal.open({
      initialTransactionId: tx.id,
    })

  const isDrawerOpen = drawerModal.isOpen
  const reRenderDrawer = drawerModal.reRender

  useEffect(() => {
    if (isDrawerOpen) {
      reRenderDrawer()
    }
  }, [dealSummary.query.data, isDrawerOpen, reRenderDrawer])

  const hasActiveFilters =
    !!search.searchTerm ||
    !!search.debouncedSearchTerm ||
    Object.entries(search.search ?? {}).some(
      ([key, value]) => key !== 'ca_search_TextSearch' && value !== undefined && value !== ''
    )

  const noResultsNode = <TransactionHistoryEmptyState hasActiveFilters={hasActiveFilters} />

  if (isMobile) {
    return (
      <Loading fullWidth showLoadingIcon isLoading={isLoading}>
        {!dealSummary.query.data.length && !isLoading
          ? noResultsNode
          : dealSummary.query.data.map((tx, index) => (
              <TransactionHistoryMobileRow
                key={tx.id}
                tx={tx}
                context='ib'
                walletId={walletId}
                id={getScrollToCardId(
                  index,
                  dealSummary.query.data.length,
                  'ib-transaction-history'
                )}
                onClick={openTransactionDrawer}
              />
            ))}
      </Loading>
    )
  }

  return (
    <IBReportsTable
      tableQuery={tableQuery}
      sortOptions={search.sortOptions}
      className={pageStyles.table}
      rowClassName={pageStyles.clickableRow}
      onRowClick={openTransactionDrawer}
      noResults={noResultsNode}
      animateNewRows
      getRowKey={(tx) => tx.id}
      renderColumns={(x: TransactionDto) => [
        <TransactionHistoryRowCells key={x.id} tx={x} context='ib' walletId={walletId} />,
      ]}
    />
  )
}
